// src/pages/EditorPage.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, Clock, Copy, MoreVertical, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import './EditorPage.css';
import { CardEditor } from '../components/CardEditor/CardEditor';
import { CardNavigation } from '../components/CardEditor/CardNavigation';
import { AddCardButton } from '../components/CardEditor/AddCardButton';
import { useClipboard } from '../contexts/ClipboardContext';
import { useAuth } from '../contexts/AuthContext';
import { useAntiPiracy } from '../hooks/useAntiPiracy';
import { ClipboardBanner } from '../components/CardEditor/ClipboardBanner';
import { ReferencesModal } from '../components/CardEditor/ReferencesModal';
import { CardErrorReport } from '../components/CardEditor/CardErrorModal';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const generateCardId = () => `card-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

interface Flashcard {
    _id?: string;
    stableId: string;
    front: string;
    back: string;
    supportContent?: string;
    references?: string;
}

interface EditorPageProps {
    isUserDeck?: boolean;
}

// ========== FUNÇÃO AUXILIAR: Extrair URLs do Cloudinary ==========
const extractCloudinaryUrls = (html: string): string[] => {
    const urls: string[] = [];
    const regex = /https:\/\/res\.cloudinary\.com\/[^\s"']+/g;
    const matches = html.match(regex);
    if (matches) {
        urls.push(...matches);
    }
    return urls;
};

// ========== FUNÇÃO AUXILIAR: Deletar mídia do Cloudinary ==========
const deleteCloudinaryMedia = async (url: string) => {
    try {
        const urlParts = url.split('/');
        const uploadIndex = urlParts.indexOf('upload');
        if (uploadIndex === -1) return;
        const afterUpload = urlParts.slice(uploadIndex + 2).join('/');
        const publicId = afterUpload.replace(/\.[^/.]+$/, '');
        const publicIdParam = publicId.replace(/\//g, '-');
        console.log('🗑️ Deletando do Cloudinary:', publicId);
        const token = localStorage.getItem('accessToken');
        await fetch(`/api/upload/cloudinary-media/${publicIdParam}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ Mídia deletada:', publicId);
    } catch (error) {
        console.error('❌ Erro ao deletar do Cloudinary:', error);
    }
};

// ========== SORTABLE CARD WRAPPER ==========
interface SortableCardWrapperProps {
    id: string;
    card: Flashcard;
    index: number;
    onFrontChange: (content: string) => void;
    onBackChange: (content: string) => void;
    onReferencesChange: (content: string) => void;
    onSupportContentChange: (content: string) => void;
    onDelete: () => void;
    onCopy: () => void;
    subdeckName: string;
    expandAllSupport: boolean;
    isRestricted?: boolean;
    errorReports?: CardErrorReport[];
    isAdmin?: boolean;
    onUpdateErrorStatus?: (errorId: string, status: 'accepted' | 'denied') => Promise<void> | void;
    onDeleteErrorReport?: (errorId: string) => Promise<void> | void;
    isHighlighted?: boolean;
}

const SortableCardWrapper: React.FC<SortableCardWrapperProps> = ({
    id,
    card,
    index,
    onFrontChange,
    onBackChange,
    onReferencesChange,
    onSupportContentChange,
    onDelete,
    onCopy,
    subdeckName,
    expandAllSupport,
    isRestricted,
    errorReports,
    isAdmin,
    onUpdateErrorStatus,
    onDeleteErrorReport,
    isHighlighted
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        marginBottom: '24px'
    };

    return (
        <div ref={setNodeRef} style={style}>
            <CardEditor
                cardNumber={index + 1}
                front={card.front}
                back={card.back}
                references={card.references}
                supportContent={card.supportContent}
                onFrontChange={onFrontChange}
                onBackChange={onBackChange}
                onReferencesChange={onReferencesChange}
                onSupportContentChange={onSupportContentChange}
                onDelete={onDelete}
                onCopy={onCopy}
                dragHandleProps={{ ...attributes, ...listeners }}
                isDragging={isDragging}
                subdeckName={subdeckName}
                expandAllSupport={expandAllSupport}
                isRestricted={isRestricted}
                errorReports={errorReports}
                isAdmin={isAdmin}
                onUpdateErrorStatus={onUpdateErrorStatus}
                onDeleteErrorReport={onDeleteErrorReport}
                isHighlighted={isHighlighted}
            />
        </div>
    );
};

const EditorPage: React.FC<EditorPageProps> = ({ isUserDeck = false }) => {
    const { deckId, subdeckId } = useParams<{ deckId: string; subdeckId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [deckName, setDeckName] = useState('');
    const [subdeckName, setSubdeckName] = useState('');
    const [subdeckDescription, setSubdeckDescription] = useState('');
    const [subdeckReferences, setSubdeckReferences] = useState('');
    const [cards, setCards] = useState<Flashcard[]>([]);
    const [loading, setLoading] = useState(true);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [cardToDelete, setCardToDelete] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isUserPath = location.pathname.includes('/my-flashcards/editor/');
    const effectiveIsUserDeck = isUserDeck || isUserPath;

    // Aplica e persiste o contexto de tema no mount
    useEffect(() => {
        const isRyan = (location.state as any)?.fromRyan === true
            || sessionStorage.getItem('graia_theme_context') === 'ryan';
        sessionStorage.setItem('graia_theme_context', isRyan ? 'ryan' : 'enem');
        if (isRyan) {
            document.body.classList.add('theme-ryan');
        } else {
            document.body.classList.remove('theme-ryan');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const currentPlatform: 'graia' | 'ryan' = (
        (location.state as any)?.fromRyan === true
        || sessionStorage.getItem('graia_theme_context') === 'ryan'
    ) ? 'ryan' : 'graia';

    const baseUrl = effectiveIsUserDeck
        ? `/api/user-content/decks/${deckId}/cards`
        : `/api/decks/${deckId}/subdecks/${subdeckId}`;

    const [selectedCardIndex, setSelectedCardIndex] = useState(0);
    const scrollRefs = useRef<(HTMLDivElement | null)[]>([]);
    const { copyCard, pasteCards, clearClipboard, count, clipboardSourcePlatform } = useClipboard();
    const { isAdmin, isEditor, loading: authLoading } = useAuth();

    // Deep-link vindo do painel de "Erros Reportados" (?cardId=...) — assim
    // que os cards carregam, rola até o card certo e destaca ele por alguns
    // segundos, em vez de deixar o admin procurar manualmente card por card.
    const targetCardId = new URLSearchParams(location.search).get('cardId');
    const [highlightedCardId, setHighlightedCardId] = useState<string | null>(null);
    const hasScrolledToTargetRef = useRef(false);

    // Erros reportados (não negados) dos cards deste subdeck, indexados por
    // cardId — alimenta o ícone de atenção no toolbar de cada CardEditor
    // (ver CardErrorModal.tsx). Um "mini painel admin" sem sair do editor.
    const [errorsByCardId, setErrorsByCardId] = useState<Map<string, CardErrorReport[]>>(new Map());
    useAntiPiracy(!authLoading && !isAdmin && !isEditor);
    const dirtyCardsRef = useRef<Set<number>>(new Set());
    const isDirtyMetaRef = useRef(false);
    const [copyAllDone, setCopyAllDone] = useState(false);
    const [showSubdeckReferencesModal, setShowSubdeckReferencesModal] = useState(false);
    const [expandAllSupport, setExpandAllSupport] = useState(false);
    const [showHeaderMenu, setShowHeaderMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowHeaderMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const stripForCopy = (html: string): string => {
        if (!html) return '';
        return html
            .replace(/<img[^>]*>/gi, '[imagem]')
            .replace(/<[^>]*>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    };

    const copyAllCards = () => {
        const text = cards.map(card => {
            const lines = [
                `Pergunta (Front): ${stripForCopy(card.front)}`,
                `Resposta (Back): ${stripForCopy(card.back)}`,
            ];
            const support = stripForCopy(card.supportContent || '');
            if (support) lines.push(`Conteúdo de apoio (Support Content): ${support}`);
            return lines.join('\n');
        }).join('\n\n');
        navigator.clipboard.writeText(text).then(() => {
            setCopyAllDone(true);
            setTimeout(() => setCopyAllDone(false), 2000);
        });
    };

    const handleSaveSubdeckReferences = (content: string) => {
        setSubdeckReferences(content);
        subdeckReferencesRef.current = content;
        setCards(prev => prev.map(card => ({ ...card, references: content })));
        isDirtyMetaRef.current = true;
    };

    const previousMediaUrlsRef = useRef<Set<string>>(new Set());

    const cardsRef = useRef<Flashcard[]>([]);
    const subdeckNameRef = useRef<string>('');
    const subdeckDescriptionRef = useRef<string>('');
    const subdeckReferencesRef = useRef<string>('');

    useEffect(() => { cardsRef.current = cards; }, [cards]);
    useEffect(() => { subdeckNameRef.current = subdeckName; }, [subdeckName]);
    useEffect(() => { subdeckDescriptionRef.current = subdeckDescription; }, [subdeckDescription]);
    useEffect(() => { subdeckReferencesRef.current = subdeckReferences; }, [subdeckReferences]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    useEffect(() => {
        loadSubdeck();
    }, [deckId, subdeckId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Erros reportados deste subdeck — carregado à parte do subdeck em si,
    // não bloqueia a edição se falhar (só o ícone de atenção some).
    useEffect(() => {
        if (effectiveIsUserDeck || !subdeckId) return;
        const token = localStorage.getItem('accessToken');
        fetch(`/api/errors/by-subdeck/${subdeckId}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.ok ? r.json() : { reports: [] })
            .then(d => {
                const map = new Map<string, CardErrorReport[]>();
                (d.reports || []).forEach((report: CardErrorReport) => {
                    if (!report.cardId) return;
                    const list = map.get(report.cardId) || [];
                    list.push(report);
                    map.set(report.cardId, list);
                });
                setErrorsByCardId(map);
            })
            .catch(() => { /* não-crítico */ });
    }, [subdeckId, effectiveIsUserDeck]);

    // Deep-link ?cardId=... (vindo do painel de erros) — assim que os cards
    // carregam, rola até o card certo e destaca ele por alguns segundos. Só
    // roda uma vez (hasScrolledToTargetRef) pra não brigar com o scroll do
    // usuário depois.
    useEffect(() => {
        if (loading || !targetCardId || hasScrolledToTargetRef.current) return;
        const index = cards.findIndex(c => c._id === targetCardId);
        if (index === -1) return;
        hasScrolledToTargetRef.current = true;
        setSelectedCardIndex(index);
        setHighlightedCardId(targetCardId);
        setTimeout(() => {
            scrollRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        const clearTimer = setTimeout(() => setHighlightedCardId(null), 4500);
        return () => clearTimeout(clearTimer);
    }, [loading, cards, targetCardId]);

    useEffect(() => {
        if (loading) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            autoSaveFromRef();
        }, 1500);
        return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
    }, [cards, subdeckName, subdeckDescription, subdeckReferences, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSubdeck = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(baseUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setDeckName(data.deckName);
                setSubdeckName(data.subdeck.name);
                setSubdeckDescription(data.subdeck.description || '');
                setSubdeckReferences(data.subdeck.references || '');
                subdeckReferencesRef.current = data.subdeck.references || '';

                const cardsWithRebuiltHTML = (data.subdeck.cards || []).map((card: any) => {
                    let front = card.front;
                    let back = card.back;
                    let supportContent = card.supportContent || '';

                    if (card.media?.images) {
                        card.media.images.forEach((img: any) => {
                            const imgHTML = `<div class="card-image-wrapper" data-side="${img.side}" style="position: relative; margin: 20px 0; text-align: center;">
    <hr style="border: none; border-top: 1px dashed rgba(47, 113, 48, 0.2); margin: 0 0 16px 0;" />
    <div class="card-image-container">
        <div class="image-thumbnail-wrapper">
            <img src="${img.url}" alt="Card" class="flashcard-img-thumbnail" style="width: 80px; height: 80px; object-fit: cover; border-radius: 6px; border: 2px solid #e5e7eb; display: block; cursor: pointer;" />
            <button type="button" class="delete-img-btn-overlay" style="position: absolute; top: -8px; right: -8px; width: 24px; height: 24px; background: #dc2626; color: white; border: 2px solid white; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; z-index: 10; box-shadow: 0 2px 6px rgba(0,0,0,0.2);">✕</button>
        </div>
    </div>
</div>`;
                            if (img.side === 'front') front += imgHTML;
                            else if (img.side === 'back') back += imgHTML;
                            else if (img.side === 'support') supportContent += imgHTML;
                        });
                    }

                    if (card.media?.audios) {
                        card.media.audios.forEach((aud: any) => {
                            const audioHTML = `<div class="card-audio-wrapper" data-side="${aud.side}" data-audio-url="${aud.url}">
                                <audio controls src="${aud.url}"></audio>
                            </div>`;
                            if (aud.side === 'front') front += audioHTML;
                            else if (aud.side === 'back') back += audioHTML;
                            else if (aud.side === 'support') supportContent += audioHTML;
                        });
                    }

                    return {
                        ...card,
                        stableId: card._id ? `stable-${card._id}` : generateCardId(),
                        front,
                        back,
                        supportContent
                    };
                });

                setCards(cardsWithRebuiltHTML);

                const currentUrls = new Set<string>();
                cardsWithRebuiltHTML.forEach((card: any) => {
                    extractCloudinaryUrls(card.front).forEach(url => currentUrls.add(url));
                    extractCloudinaryUrls(card.back).forEach(url => currentUrls.add(url));
                    extractCloudinaryUrls(card.supportContent || '').forEach(url => currentUrls.add(url));
                });
                previousMediaUrlsRef.current = currentUrls;
            }

        } catch (error) {
            console.error('Erro ao carregar subdeck:', error);
        } finally {
            setLoading(false);
        }
    };

    const autoSaveFromRef = async () => {
        const currentCards = cardsRef.current;
        const dirtyIndexes = Array.from(dirtyCardsRef.current);
        const currentName = subdeckNameRef.current;
        const currentDescription = subdeckDescriptionRef.current;
        const currentReferences = subdeckReferencesRef.current;
        const hasNewCards = dirtyIndexes.some(i => !currentCards[i]?._id);
        const saveMeta = isDirtyMetaRef.current;

        if (dirtyIndexes.length === 0 && !saveMeta) return;

        dirtyCardsRef.current.clear();
        isDirtyMetaRef.current = false;

        if (hasNewCards) {
            await autoSave(currentCards, currentName, currentDescription);
            return;
        }

        setSaving(true);
        const token = localStorage.getItem('accessToken');

        try {
            const extractMediaUrls = (html: string) => {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;
                const images: Array<{ url: string; side: string | null }> = [];
                tempDiv.querySelectorAll('.card-image-wrapper').forEach((wrapper) => {
                    const img = wrapper.querySelector('.flashcard-img-thumbnail') as HTMLImageElement;
                    const side = (wrapper as HTMLElement).dataset.side;
                    if (img?.src) images.push({ url: img.src, side: side || null });
                });
                const audios: Array<{ url: string; side: string | null }> = [];
                tempDiv.querySelectorAll('.card-audio-wrapper').forEach((wrapper) => {
                    const side = (wrapper as HTMLElement).dataset.side;
                    const audioUrl = (wrapper as HTMLElement).dataset.audioUrl;
                    if (audioUrl) audios.push({ url: audioUrl, side: side || null });
                });
                tempDiv.querySelectorAll('.card-image-wrapper').forEach(el => el.remove());
                tempDiv.querySelectorAll('.card-audio-wrapper').forEach(el => el.remove());
                return { cleanHtml: tempDiv.innerHTML, images, audios };
            };

            const promises: Promise<any>[] = [];

            if (saveMeta) {
                const metaUrl = effectiveIsUserDeck
                    ? `/api/user-content/decks/${deckId}/meta`
                    : `/api/decks/${deckId}/subdecks/${subdeckId}/meta`;
                promises.push(
                    fetch(metaUrl, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                            name: currentName,
                            description: currentDescription,
                            references: currentReferences
                        })
                    })
                );
            }

            dirtyIndexes.forEach((index) => {
                const c = currentCards[index];
                if (!c?._id) return;

                const frontData = extractMediaUrls(c.front);
                const backData = extractMediaUrls(c.back);
                const supportData = extractMediaUrls(c.supportContent || '');

                const cardUrl = effectiveIsUserDeck
                    ? `/api/user-content/decks/${deckId}/cards/${c._id}`
                    : `/api/decks/${deckId}/subdecks/${subdeckId}/cards/${c._id}`;

                promises.push(
                    fetch(cardUrl, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                            front: frontData.cleanHtml,
                            back: backData.cleanHtml,
                            supportContent: supportData.cleanHtml,
                            references: c.references || '',
                            media: {
                                images: [
                                    ...frontData.images.map(img => ({ url: img.url, side: img.side || 'front' })),
                                    ...backData.images.map(img => ({ url: img.url, side: img.side || 'back' })),
                                    ...supportData.images.map(img => ({ url: img.url, side: img.side || 'support' })),
                                ],
                                audios: [
                                    ...frontData.audios.map(a => ({ url: a.url, side: a.side || 'front' })),
                                    ...backData.audios.map(a => ({ url: a.url, side: a.side || 'back' })),
                                    ...supportData.audios.map(a => ({ url: a.url, side: a.side || 'support' })),
                                ]
                            }
                        })
                    })
                );
            });

            await Promise.all(promises);
            setLastSaved(new Date());
        } catch (error) {
            console.error('❌ [AutoSave] Erro:', error);
        } finally {
            setSaving(false);
        }
    };

    const autoSave = async (
        currentCards: Flashcard[],
        currentSubdeckName: string,
        currentSubdeckDescription: string
    ) => {
        setSaving(true);

        try {
            const token = localStorage.getItem('accessToken');

            console.log('💾 [AutoSave] Iniciando...');

            const extractMediaUrls = (html: string) => {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;

                const imgWrappers = tempDiv.querySelectorAll('.card-image-wrapper');
                const images: Array<{ url: string; side: string | null }> = [];

                imgWrappers.forEach((wrapper) => {
                    const img = wrapper.querySelector('.flashcard-img-thumbnail') as HTMLImageElement;
                    const side = (wrapper as HTMLElement).dataset.side;
                    if (img?.src) {
                        images.push({ url: img.src, side: side || null });
                    }
                });

                const audioWrappers = tempDiv.querySelectorAll('.card-audio-wrapper');
                const audios: Array<{ url: string; side: string | null }> = [];

                audioWrappers.forEach((wrapper) => {
                    const side = (wrapper as HTMLElement).dataset.side;
                    const audioUrl = (wrapper as HTMLElement).dataset.audioUrl;
                    if (audioUrl) {
                        audios.push({ url: audioUrl, side: side || null });
                    }
                });

                tempDiv.querySelectorAll('.card-image-wrapper').forEach(el => el.remove());
                tempDiv.querySelectorAll('.card-audio-wrapper').forEach(el => el.remove());

                return { cleanHtml: tempDiv.innerHTML, images, audios };
            };

            const processedCards = currentCards.map((c, index) => {
                const frontData = extractMediaUrls(c.front);
                const backData = extractMediaUrls(c.back);
                const supportData = extractMediaUrls(c.supportContent || '');

                const allImages = [
                    ...frontData.images.map(img => ({ ...img, source: 'front' })),
                    ...backData.images.map(img => ({ ...img, source: 'back' })),
                    ...supportData.images.map(img => ({ ...img, source: 'support' }))
                ];

                const allAudios = [
                    ...frontData.audios.map(aud => ({ ...aud, source: 'front' })),
                    ...backData.audios.map(aud => ({ ...aud, source: 'back' })),
                    ...supportData.audios.map(aud => ({ ...aud, source: 'support' }))
                ];

                return {
                    _id: c._id,
                    front: frontData.cleanHtml,
                    back: backData.cleanHtml,
                    supportContent: supportData.cleanHtml,
                    references: c.references,
                    media: {
                        images: allImages.map(img => ({ url: img.url, side: img.side || img.source })),
                        audios: allAudios.map(aud => ({ url: aud.url, side: aud.side || aud.source }))
                    },
                    order: index
                };
            });

            const currentUrls = new Set<string>();
            currentCards.forEach((card) => {
                extractCloudinaryUrls(card.front).forEach(url => currentUrls.add(url));
                extractCloudinaryUrls(card.back).forEach(url => currentUrls.add(url));
                extractCloudinaryUrls(card.supportContent || '').forEach(url => currentUrls.add(url));
            });

            const orphanedUrls = Array.from(previousMediaUrlsRef.current).filter(
                url => !currentUrls.has(url)
            );

            if (orphanedUrls.length > 0) {
                console.log('🗑️ [AutoSave] Mídias órfãs detectadas:', orphanedUrls.length);
                for (const url of orphanedUrls) {
                    await deleteCloudinaryMedia(url);
                }
            }

            previousMediaUrlsRef.current = currentUrls;

            const payload = {
                name: currentSubdeckName,
                description: currentSubdeckDescription,
                references: subdeckReferencesRef.current,
                cards: processedCards
            };

            const response = await fetch(baseUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setLastSaved(new Date());
                console.log('✅ [AutoSave] Salvo com sucesso');
            } else {
                const errorData = await response.json();
                console.error('❌ [AutoSave] Erro do servidor:', errorData);
            }
        } catch (error) {
            console.error('❌ [AutoSave] Erro de rede:', error);
        } finally {
            setSaving(false);
        }
    };

    const addCard = () => {
        setCards(prev => [...prev, { stableId: generateCardId(), front: '', back: '', supportContent: '', references: '' }]);
    };

    const handleDeleteCard = (index: number) => {
        setCardToDelete(index);
        setShowDeleteDialog(true);
    };

    const handleCopyCard = (index: number) => {
        copyCard(cards[index]);
    };

    // Mesmas rotas usadas pelo painel admin (server/routes/errorRoutes.js) —
    // ver CardErrorModal.tsx (mini painel dentro do editor).
    const handleUpdateErrorStatus = async (errorId: string, status: 'accepted' | 'denied') => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/errors/${errorId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ status })
            });
            if (!res.ok) return;
            setErrorsByCardId(prev => {
                const next = new Map(prev);
                for (const [cardId, reports] of next) {
                    const updated = reports.map(r => r._id === errorId ? { ...r, status } : r);
                    if (status === 'denied') {
                        next.set(cardId, updated.filter(r => r._id !== errorId));
                    } else {
                        next.set(cardId, updated);
                    }
                }
                return next;
            });
        } catch (error) {
            console.error('Erro ao atualizar status do report:', error);
        }
    };

    const handleDeleteErrorReport = async (errorId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`/api/errors/${errorId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) return;
            setErrorsByCardId(prev => {
                const next = new Map(prev);
                for (const [cardId, reports] of next) {
                    next.set(cardId, reports.filter(r => r._id !== errorId));
                }
                return next;
            });
        } catch (error) {
            console.error('Erro ao excluir report:', error);
        }
    };

    const handlePasteCards = async () => {
        const cardsToPaste = await pasteCards();
        if (cardsToPaste.length === 0) return;

        const newCards = cardsToPaste.map(card => ({
            stableId: generateCardId(),
            front: card.front,
            back: card.back,
            supportContent: card.supportContent || '',
            references: card.references || ''
        }));

        setCards(prev => {
            const updated = [...prev, ...newCards];
            setTimeout(() => {
                autoSave(updated, subdeckNameRef.current, subdeckDescriptionRef.current);
            }, 300);
            return updated;
        });
    };

    const confirmDeleteCard = async () => {
        if (cardToDelete === null) return;

        const cardBeingDeleted = cards[cardToDelete];

        const urlsToDelete = new Set<string>();
        extractCloudinaryUrls(cardBeingDeleted.front).forEach(url => urlsToDelete.add(url));
        extractCloudinaryUrls(cardBeingDeleted.back).forEach(url => urlsToDelete.add(url));
        extractCloudinaryUrls(cardBeingDeleted.supportContent || '').forEach(url => urlsToDelete.add(url));

        const newCards = cards.filter((_, i) => i !== cardToDelete);
        setCards(newCards);
        setCardToDelete(null);
        setShowDeleteDialog(false);

        dirtyCardsRef.current.clear();
        isDirtyMetaRef.current = false;
        await autoSave(newCards, subdeckNameRef.current, subdeckDescriptionRef.current);

        if (urlsToDelete.size > 0) {
            for (const url of urlsToDelete) await deleteCloudinaryMedia(url);
        }
    };

    const cancelDelete = () => {
        setCardToDelete(null);
        setShowDeleteDialog(false);
    };

    const handleCardReorder = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = cards.findIndex(c => c.stableId === active.id);
        const newIndex = cards.findIndex(c => c.stableId === over.id);
        const reorderedCards = arrayMove(cards, oldIndex, newIndex);
        setCards(reorderedCards);
    };

    const addCardAt = (index: number) => {
        const newCard: Flashcard = {
            stableId: generateCardId(),
            front: '',
            back: '',
            supportContent: '',
            references: ''
        };
        setCards(prev => {
            const updated = [...prev];
            updated.splice(index, 0, newCard);
            return updated;
        });
    };

    const handleBack = () => {
        if (effectiveIsUserDeck) {
            const ctx = sessionStorage.getItem('userEditorContext');
            const collectionId = ctx
                ? (JSON.parse(ctx) as { collectionId?: string }).collectionId
                : undefined;
            navigate(collectionId ? `/my-flashcards/${collectionId}` : '/my-flashcards');
            return;
        }

        // Detectar contexto Ryan
        const fromRyan = (location.state as any)?.fromRyan === true;
        const ryanReturnPath = (location.state as any)?.returnPath as string | undefined;
        if (fromRyan && ryanReturnPath) {
            sessionStorage.removeItem(`subdecks_${deckId}`);
            navigate(ryanReturnPath);
            return;
        }

        sessionStorage.removeItem('graia_theme_context');
        const savedContext = sessionStorage.getItem('subdecksContext');
        const context = savedContext ? JSON.parse(savedContext) : {};
        sessionStorage.removeItem(`subdecks_${deckId}`);
        navigate(`/subdecks/${deckId}`, { state: { ...context, refreshSubdeck: subdeckId } });
    };

    if (loading) {
        return <div className="edtp-loading">Carregando...</div>;
    }

    return (
        <div className="edtp-page">
            <header className="edtp-header">
                <div className="edtp-header-content">

                    {/* ── Linha 1: navegação ── */}
                    <div className="edtp-header-nav">
                        <button className="edtp-btn-back" onClick={handleBack} title="Voltar">
                            <ArrowLeft size={18} />
                        </button>

                        <span className="edtp-deck-title">{deckName || 'Editor'}</span>

                        <div className="edtp-header-controls">
                            <div className="edtp-autosave-status">
                                {saving && (
                                    <div className="edtp-status edtp-status-saving">
                                        <Clock size={11} />
                                        <span>Salvando</span>
                                    </div>
                                )}
                                {!saving && lastSaved && (
                                    <div className="edtp-status edtp-status-saved">
                                        <Check size={11} />
                                        <span>{lastSaved.toLocaleTimeString()}</span>
                                    </div>
                                )}
                                {!saving && !lastSaved && (
                                    <div className="edtp-status edtp-status-waiting">
                                        <Clock size={11} />
                                        <span>Aguardando</span>
                                    </div>
                                )}
                            </div>

                            {!effectiveIsUserDeck && (
                                <div className="edtp-menu-wrapper" ref={menuRef}>
                                    <button
                                        className="edtp-btn-menu"
                                        onClick={() => setShowHeaderMenu(!showHeaderMenu)}
                                        title="Menu"
                                    >
                                        <MoreVertical size={17} />
                                    </button>

                                    {showHeaderMenu && (
                                        <div className="edtp-menu-dropdown">
                                            <button
                                                className={`edtp-menu-item${copyAllDone ? ' edtp-menu-item--done' : ''}`}
                                                onClick={() => { copyAllCards(); setShowHeaderMenu(false); }}
                                                disabled={cards.length === 0}
                                            >
                                                <Copy size={14} />
                                                <span>{copyAllDone ? 'Copiado!' : `Copiar todos (${cards.length})`}</span>
                                            </button>

                                            <button
                                                className="edtp-menu-item"
                                                onClick={() => { setShowSubdeckReferencesModal(true); setShowHeaderMenu(false); }}
                                            >
                                                <BookOpen size={14} />
                                                <span>Referências{subdeckReferences ? ' ✓' : ''}</span>
                                            </button>

                                            <button
                                                className="edtp-menu-item"
                                                onClick={() => { setExpandAllSupport(v => !v); setShowHeaderMenu(false); }}
                                            >
                                                {expandAllSupport
                                                    ? <ChevronUp size={14} />
                                                    : <ChevronDown size={14} />
                                                }
                                                <span>{expandAllSupport ? 'Recolher apoio' : 'Expandir apoio'}</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Linha 2: edição de nome e descrição ── */}
                    <div className="edtp-header-edit">
                        <input
                            type="text"
                            className="edtp-subdeck-input"
                            value={subdeckName}
                            onChange={(e) => {
                                isDirtyMetaRef.current = true;
                                setSubdeckName(e.target.value);
                            }}
                            placeholder="Nome do subdeck"
                        />
                        <input
                            type="text"
                            className="edtp-description-input"
                            value={subdeckDescription}
                            onChange={(e) => {
                                isDirtyMetaRef.current = true;
                                setSubdeckDescription(e.target.value);
                            }}
                            placeholder="Descrição (opcional)"
                        />
                    </div>

                </div>
            </header>

            <ClipboardBanner
                count={count}
                onPaste={handlePasteCards}
                onClear={clearClipboard}
                sourcePlatform={clipboardSourcePlatform}
                currentPlatform={currentPlatform}
            />

            {cards.length > 0 && (
                <div className="edtp-nav-wrapper">
                    <CardNavigation
                        totalCards={cards.length}
                        currentCard={selectedCardIndex}
                        onCardClick={(index: number) => {
                            setSelectedCardIndex(index);
                            scrollRefs.current[index]?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }}
                    />
                </div>
            )}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleCardReorder}
            >
                <SortableContext
                    items={cards.map(c => c.stableId)}
                    strategy={verticalListSortingStrategy}
                >
                    <div className="edtp-cards-container">
                        {cards.map((card, index) => (
                            <React.Fragment key={card.stableId}>
                                <AddCardButton onClick={() => addCardAt(index)} />

                                <div ref={(el) => { scrollRefs.current[index] = el; }}>
                                    <SortableCardWrapper
                                        key={card.stableId}
                                        id={card.stableId}
                                        card={card}
                                        index={index}
                                        expandAllSupport={expandAllSupport}
                                        onFrontChange={(content) => {
                                            dirtyCardsRef.current.add(index);
                                            const updated = cards.map((card, i) =>
                                                i === index ? { ...card, front: content } : card
                                            );
                                            setCards(updated);
                                        }}
                                        onBackChange={(content) => {
                                            dirtyCardsRef.current.add(index);
                                            const updated = cards.map((card, i) =>
                                                i === index ? { ...card, back: content } : card
                                            );
                                            setCards(updated);
                                        }}
                                        onReferencesChange={(content) => {
                                            dirtyCardsRef.current.add(index);
                                            const updated = cards.map((card, i) =>
                                                i === index ? { ...card, references: content } : card
                                            );
                                            setCards(updated);
                                        }}
                                        onSupportContentChange={(content) => {
                                            dirtyCardsRef.current.add(index);
                                            const updated = cards.map((card, i) =>
                                                i === index ? { ...card, supportContent: content } : card
                                            );
                                            setCards(updated);
                                        }}
                                        onDelete={() => handleDeleteCard(index)}
                                        onCopy={() => handleCopyCard(index)}
                                        subdeckName={subdeckName}
                                        isRestricted={effectiveIsUserDeck}
                                        errorReports={card._id ? errorsByCardId.get(card._id) : undefined}
                                        isAdmin={isAdmin}
                                        onUpdateErrorStatus={handleUpdateErrorStatus}
                                        onDeleteErrorReport={handleDeleteErrorReport}
                                        isHighlighted={!!card._id && card._id === highlightedCardId}
                                    />
                                </div>
                            </React.Fragment>
                        ))}

                    </div>
                </SortableContext>
            </DndContext>

            {cards.length > 0 && (
                <div className="edtp-cards-container">
                    <AddCardButton onClick={() => addCardAt(cards.length)} />
                </div>
            )}

            {cards.length === 0 && (
                <div className="edtp-cards-container">
                    <button className="edtp-btn-add-first" onClick={addCard}>
                        + Adicionar primeiro card
                    </button>
                </div>
            )}

            <ConfirmDialog
                isOpen={showDeleteDialog}
                title="Excluir Card"
                message="Tem certeza que deseja excluir este card? Esta ação não pode ser desfeita."
                onConfirm={confirmDeleteCard}
                onCancel={cancelDelete}
                confirmText="Excluir"
                cancelText="Cancelar"
            />

            <ReferencesModal
                isOpen={showSubdeckReferencesModal}
                initialContent={subdeckReferences}
                onSave={handleSaveSubdeckReferences}
                onClose={() => setShowSubdeckReferencesModal(false)}
            />
        </div>
    );
};

export default EditorPage;
